import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useReducer,
  useRef,
} from 'react';
import type { FunctionComponent } from 'react';
import type { ValueOf } from 'type-fest';

import { isActionToast, isLinkToast, isSurveyToast } from '../components/Toaster/utils';
import type { AddToast, NewToast, Toast } from '../components/Toaster/types';

//  Timeout for regular toasts.
const TOAST_TIMEOUT = 3 * 1000; // 3s
//  Timeout for the slide out animation.
const TOAST_ANIMATION_TIMEOUT = 1 * 1000; // 1s

export type ToastContextState = {
  toasts: Toast[];
  /**
   * The primary way to add Toasts. The object provided determines what type of toast will be created.
   *
   * @param {string} toast.type - The type of toast. Supported levels are `info`, `success`, and `error`.
   * @param {string} toast.content - The content to display in the toast.
   * @param {string} [toast.link] - (Optional) A link to an internal FOSSA page. If this is provided, a LinkToast will be rendered.
   * @param {object} [toast.action] - (Optional) An object describing the action that should happen on Button press. If this is provided, an ActionToast will be rendered.
   *                                  Takes in the `text` for the Button and the `onAction` callback.
   * @param {boolean} [toast.persistent] - (Optional) If true, the toast never auto-dismisses and survives navigation.
   * @returns The `id` of the Toast. Can be used to programatically delete the Toast later with `removeToast`.
   */
  addToast: AddToast;
  //  Removes a toast, visually animating it out before removing it from the DOM.
  hideToast: (id: number) => void;
  clearToasts: () => void;
  /** Clears every toast except survey and persistent toasts, which survive navigation. */
  clearTransientToasts: () => void;
};

const TOAST_ACTIONS = {
  ADD: 'ADD',
  HIDE: 'HIDE',
  REMOVE: 'REMOVE',
  CLEAR: 'CLEAR',
  CLEAR_TRANSIENT: 'CLEAR_TRANSIENT',
} as const;

type Action =
  | { type: ValueOf<typeof TOAST_ACTIONS, 'ADD'>; toast: Toast }
  | { type: ValueOf<typeof TOAST_ACTIONS, 'HIDE'>; id: number }
  | { type: ValueOf<typeof TOAST_ACTIONS, 'REMOVE'>; id: number } // Note this action is only used internally and isn't exposed. Only `hide` is exposed.
  | { type: ValueOf<typeof TOAST_ACTIONS, 'CLEAR'>; }
  | { type: ValueOf<typeof TOAST_ACTIONS, 'CLEAR_TRANSIENT'>; };

//  Create the context.
export const ToastContext = createContext<ToastContextState>({} as ToastContextState);

/**
 * Hook for the entire Toasts context.
 * NOTE: do NOT use this directly, this should only be consumed by Toaster (the toast manager).
 */
export const useToasts = () => useContext(ToastContext);

/**
 * Hook which allows you to simply add a new toast to the page.
 * @example
 * const MyComponent = () => {
 *   const addToast = useAddToast();
 *
 *   return (
 *     <Button onClick={() => addToast({ content: 'Hello world!' })}>
 *       Add toast!
 *     </Button>
 *   );
 * };
 */
export const useAddToast = (): (toast: NewToast) => number => {
  const { addToast } = useContext(ToastContext);
  return addToast;
};

const reducer = (state: Toast[], action: Action): Toast[] => {
  if (action.type === TOAST_ACTIONS.ADD) {
    return [...state, action.toast];
  }

  //  Visually hide the toast (ie: make it slide out).
  if (action.type === TOAST_ACTIONS.HIDE) {
    return state.map(toast => toast.id !== action.id ? toast : ({
      ...toast,
      isVisible: false,
    }));
  }

  if (action.type === TOAST_ACTIONS.REMOVE) {
    return state.filter(toast => toast.id !== action.id);
  }

  if (action.type === TOAST_ACTIONS.CLEAR) {
    return [];
  }

  if (action.type === TOAST_ACTIONS.CLEAR_TRANSIENT) {
    return state.filter((toast) => isSurveyToast(toast) || toast.persistent === true);
  }

  return state;
};

//  By default this is what DevRev sets the widget to.
const MAX_ZINDEX_VALUE = 2147483647;
const DEV_REV_WIDGET_SELECTOR = 'iframe[src*="plug-platform.devrev.ai"]';

/**
 * Find the DevRev iFrame and override its aggressive z-index.
 *
 * DISCLAIMER: This is not at all The React Way™️, we are only doing this kind
 * of explicit element manipulation because we don't control the DevRev widget.
 */
function hideDevRev() {
  const iframe = document.querySelector<HTMLIFrameElement>(DEV_REV_WIDGET_SELECTOR);
  if (iframe) {
    iframe.style.zIndex = '0';
  }
}

/**
 * Find the DevRev iFrame and reset its aggressive z-index.
 *
 * DISCLAIMER: This is not at all The React Way™️, we are only doing this kind
 * of explicit element manipulation because
 */
function resetDevRev() {
  const iframe = document.querySelector<HTMLIFrameElement>(DEV_REV_WIDGET_SELECTOR);
  if (iframe) {
    iframe.style.zIndex = `${MAX_ZINDEX_VALUE}`; // String expected
  }
}

/**
 * Wraps the application to provide access to the ToastContext to all children.
 */
const ToastContextProvider: FunctionComponent = ({ children }) => {
  const [toasts, dispatch] = useReducer<typeof reducer>(reducer, []);
  const nextIdRef = useRef(0);

  const removeToast = useCallback((id: number) => {
    dispatch({ type: TOAST_ACTIONS.REMOVE, id });
  }, []);

  //  This is only used to visually "hide" the toast with an animation.
  //  It then kicks off a timeout to actually remove the toasts from state.
  const hideToast = useCallback((id: number) => {
    dispatch({ type: TOAST_ACTIONS.HIDE, id });

    //  After the animation is done we can remove the toast from state. The
    //  reactive effect below will reset the DevRev z-index once no toasts
    //  remain visible.
    setTimeout(() => removeToast(id), TOAST_ANIMATION_TIMEOUT);
  }, [removeToast]);

  //  Reset DevRev's z-index whenever no toasts remain. Handles every dismissal
  //  path — hideToast's animation-out, clearToasts, clearTransientToasts when
  //  no survey toast survives — without each handler needing to know about the
  //  DevRev iframe directly.
  useEffect(() => {
    if (toasts.length === 0) {
      resetDevRev();
    }
  }, [toasts.length]);

  // Synchronous: generates a monotonic id, dispatches immediately, and returns
  // the id so callers can store it (e.g. SurveyProvider's onSurveySubmit
  // closure needs the id to call `hideToast` later). The previous debounced
  // dispatch dropped rapid successive calls — fine when only one was ever in
  // flight, but it collapsed `surveys.forEach(dispatchSurveyToast)` into a
  // single toast.
  const addToast = useCallback((newToast: NewToast): number => {
    const id = Date.now() + nextIdRef.current;
    nextIdRef.current += 1;
    const toast = { ...newToast, id, isVisible: true } as Toast;

    //  Lower the z-index value for the DevRev widget so it doesn't get in the way.
    hideDevRev();

    dispatch({ type: TOAST_ACTIONS.ADD, toast });

    //  Link, Action, Survey and persistent toasts do not auto-dismiss; regular
    //  toasts are dismissed after the TOAST_TIMEOUT.
    if (!toast.persistent && !isLinkToast(toast) && !isActionToast(toast) && !isSurveyToast(toast)) {
      setTimeout(() => hideToast(toast.id), TOAST_TIMEOUT);
    }
    return id;
  }, [hideToast]);

  const clearToasts = useCallback(() => {
    dispatch({ type: TOAST_ACTIONS.CLEAR });
  }, []);

  const clearTransientToasts = useCallback(() => {
    dispatch({ type: TOAST_ACTIONS.CLEAR_TRANSIENT });
  }, []);

  const contextValue = useMemo((): ToastContextState => ({
    toasts,
    addToast,
    //  Note that we don't expose the actual removeToast method,
    //  because we want all toasts to be animated out properly.
    hideToast,
    clearToasts,
    clearTransientToasts,
  }), [addToast, hideToast, clearToasts, clearTransientToasts, toasts]);

  return (
    <ToastContext.Provider value={contextValue}>
      {children}
    </ToastContext.Provider>
  );
};

export default ToastContextProvider;
